import { motion, useReducedMotion } from "framer-motion";

/**
 * Fade + rise scroll-reveal wrapper. Wraps any block-level content.
 * Respects prefers-reduced-motion (renders instantly, no transform).
 *
 * Props:
 *  - as: motion element tag (default "div")
 *  - delay: seconds
 *  - y: initial vertical offset in px (default 22)
 *  - once: only animate the first time it enters view (default true)
 */
export function Reveal({
  as = "div",
  delay = 0,
  y = 22,
  once = true,
  amount = 0.2,
  className,
  children,
  ...rest
}) {
  const shouldReduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;

  if (shouldReduce) {
    const Plain = as;
    return (
      <Plain className={className} {...rest}>
        {children}
      </Plain>
    );
  }

  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

/** Stagger container: animates children's `data-reveal-item` via variants. */
export function RevealGroup({ as = "div", className, stagger = 0.08, delay = 0, children, ...rest }) {
  const shouldReduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;

  if (shouldReduce) {
    const Plain = as;
    return (
      <Plain className={className} {...rest}>
        {children}
      </Plain>
    );
  }

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

export const revealItemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

export default Reveal;
